import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { ApiConfig } from "@/config/api.config";
import { ApiHookProps } from '@/@types/api.types';
import { UrlHelper } from '@/utils/UrlHelper';
import { OrderListItemResponseDtoListResponseDto } from '@/features/orders/types/orders-dto';
import { useOrdersFilter } from '@/features/orders/hooks/useOrdersFilter';
import { NumbersHelper } from '@/utils/numbersHelper';
import { DateTimeHelper } from '@/utils/dateTimeHelper';
import { useAxiosAuth } from '@/hooks/useAxiosAuth';
import { useUserRole } from '@/features/identity/hooks/useUserRole.ts';
import { Roles } from '@/@types/auth.types.ts';

type Props = {
    page?: number;
    listSize?: number;
} & ApiHookProps;

export const useOrdersListApi = ({ page, listSize }: Props = {}) => {
    const axiosInstance = useAxiosAuth();
    const userRole = useUserRole();
    const { filter } = useOrdersFilter();
    const { take, skip, startDate, endDate, authorId, apartmentId, serviceId, orderStatus, amountRange, orderBy, orderDirection } = filter;

    const authorIds =
        userRole === Roles.admin ? authorId?.map((author) => author.id) : undefined;
    const apartmentIds = apartmentId?.map((apartment) => apartment.id);
    const serviceIds = serviceId?.map((service) => service.id);
    const {min, max} = NumbersHelper.getMinMaxValues(amountRange);

    const {
        data: ordersListData, 
        isLoading: isLoadingOrdersListData, 
        isError: isErrorOrdersListData,       
    } = useQuery({
        queryKey: [
            ApiConfig.queries.features.hotels.queryKeys.apiRefresh, 
            ApiConfig.queries.features.orders.queryKeys.list, 
            page, take, skip, startDate, endDate, userRole, authorId, apartmentId, serviceId, orderStatus, amountRange, orderBy, orderDirection, listSize
        ],
        queryFn: async (): Promise<OrderListItemResponseDtoListResponseDto> => {
            const url = UrlHelper.buildUrlWithQueryParams(ApiConfig.queries.features.orders.endpoints.list, {    
                startDate: DateTimeHelper.getDateOnly(startDate), 
                endDate: DateTimeHelper.getDateOnly(endDate), 
                ...(userRole === Roles.admin ? { authorId: authorIds } : {}),
                apartmentId: apartmentIds, 
                serviceId: serviceIds, 
                minAmount: min, 
                maxAmount: max, 
                take: listSize ?? take, 
                skip: typeof page === 'number' && page >= 0 ? page * take : skip, 
                orderBy, orderDirection, orderStatus
            });
            const response = await axiosInstance.get<OrderListItemResponseDtoListResponseDto>(url);
            return response.data;
        },
        placeholderData: keepPreviousData
    });

    return {
        ordersListData,
        isLoadingOrdersListData,
        isErrorOrdersListData,
    }
};
